import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";

const ThemeSettingsContext = createContext(null);

export function useThemeSettings() {
  const ctx = useContext(ThemeSettingsContext);
  if (!ctx) {
    throw new Error("useThemeSettings must be used within AppThemeProvider");
  }
  return ctx;
}

export function AppThemeProvider({ children }) {
  const [mode, setMode] = useState("dark");
  const [highContrast, setHighContrast] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("exi-theme-settings");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.mode) setMode(parsed.mode);
        if (typeof parsed.highContrast === "boolean") {
          setHighContrast(parsed.highContrast);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Persist to localStorage
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "exi-theme-settings",
        JSON.stringify({ mode, highContrast })
      );
    } catch {
      // ignore
    }
  }, [mode, highContrast]);

  const theme = useMemo(() => {
    // White and Dark Blue theme
    const basePrimary = "#1e3a8a"; // Dark Blue (Indigo 900)
    const bgDefault = "#ffffff";
    const bgPaper = "#f8fafc"; // Very light gray/blue for paper elements

    return createTheme({
      palette: {
        mode,
        background: { 
          default: bgDefault, 
          paper: bgPaper 
        },
        primary: { 
          main: basePrimary,
          light: "#3b82f6",
          dark: "#1e3a8a",
          contrastText: "#ffffff",
        },
        secondary: { 
          main: "#3b82f6",
          light: "#60a5fa",
          dark: "#2563eb",
        },
        text: {
          primary: "#0f172a",
          secondary: "#475569",
        },
        divider: "#e2e8f0",
      },
      typography: {
        fontFamily: "'Inter', 'Roboto', sans-serif",
      },
      shape: {
        borderRadius: 10,
      },
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: "none",
            },
          },
        },
      },
    });
  }, [mode, highContrast]);

  const value = useMemo(
    () => ({
      mode,
      setMode,
      highContrast,
      setHighContrast,
    }),
    [mode, highContrast]
  );

  return (
    <ThemeSettingsContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeSettingsContext.Provider>
  );
}


